import { api } from './api'
import { SystemSetting, SettingUpdate, SettingCreate, ApiResponse } from '@/types'

export const settingsService = {
  /**
   * Get all public settings (no auth required)
   */
  async getPublicSettings(): Promise<SystemSetting[]> {
    const response = await api.get<ApiResponse<SystemSetting[]>>('/settings/public')
    return response.data.data || []
  },

  /**
   * Get all settings (admin only)
   */
  async getAllSettings(): Promise<SystemSetting[]> {
    const response = await api.get<ApiResponse<SystemSetting[]>>('/settings')
    return response.data.data || []
  },

  /**
   * Get a specific setting by key (admin only)
   */
  async getSettingByKey(key: string): Promise<SystemSetting> {
    const response = await api.get<ApiResponse<SystemSetting>>(`/settings/${key}`)
    if (!response.data.data) {
      throw new Error(`Setting not found: ${key}`)
    }
    return response.data.data
  },

  /**
   * Get settings by category (admin only)
   */
  async getSettingsByCategory(category: string): Promise<SystemSetting[]> {
    const response = await api.get<ApiResponse<SystemSetting[]>>(`/settings/category/${category}`)
    return response.data.data || []
  },

  /**
   * Update a single setting (admin only)
   */
  async updateSetting(key: string, value: string): Promise<SystemSetting> {
    const response = await api.patch<ApiResponse<SystemSetting>>(`/settings/${key}`, { value })
    if (!response.data.data) {
      throw new Error(`Failed to update setting: ${key}`)
    }
    return response.data.data
  },

  /**
   * Batch update multiple settings (admin only)
   */
  async updateSettings(updates: SettingUpdate[]): Promise<SystemSetting[]> {
    const response = await api.patch<ApiResponse<SystemSetting[]>>('/settings', { updates })
    return response.data.data || []
  },

  /**
   * Create a new setting (admin only)
   */
  async createSetting(data: SettingCreate): Promise<SystemSetting> {
    const response = await api.post<ApiResponse<SystemSetting>>('/settings', data)
    if (!response.data.data) {
      throw new Error('Failed to create setting')
    }
    return response.data.data
  },

  /**
   * Delete a setting (admin only)
   */
  async deleteSetting(key: string): Promise<void> {
    await api.delete(`/settings/${key}`)
  },
}
